import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { fetchShippingQuote } from '../../../actions'
import { fetchShippingRates } from '../../../redux/storeConfig/slices/slice.checkoutRates'
import { isShipperHQActive } from '../../../util/integrations'
import { shouldCalculateShipping, createShippingParams } from '../services/shippingCalculationService'
import { buildShippingParams } from '../services/shippingParamsBuilder'

/**
 * Custom hook to handle shipping calculation
 * @param {object} params - Shipping calculation parameters
 * @param {boolean} params.isInitializing - Whether checkout is initializing
 * @param {boolean} params.isCartLoaded - Whether cart is loaded
 * @param {Array} params.cartItems - Array of cart items
 * @param {object} params.checkout - Checkout state object
 * @param {object} params.portal - Portal configuration
 * @param {object} params.integrations - Integrations configuration
 * @param {object} params.checkoutRates - Checkout rates state
 * @returns {object} - { calculateShipping: function }
 */
export const useShippingCalculation = ({
  isInitializing,
  isCartLoaded,
  cartItems,
  checkout,
  portal,
  integrations,
  checkoutRates
}) => {
  const dispatch = useDispatch()

  // Extract primitive values and specific properties to prevent unnecessary re-renders
  const { selectedCustomerAddress, multiAddressSelection } = checkout || {}

  const { checkout_mode: checkoutMode, multiple_address_shipping: multipleAddressShipping } = portal || {}

  const integrationsData = integrations?.data

  // Create stable serialized keys for complex objects/arrays
  const selectedAddressKey = useMemo(() => JSON.stringify(selectedCustomerAddress), [selectedCustomerAddress])
  const multiAddressSelectionKey = useMemo(() => JSON.stringify(multiAddressSelection), [multiAddressSelection])
  const cartItemsKey = useMemo(() => JSON.stringify(cartItems), [cartItems])
  const integrationsDataKey = useMemo(() => JSON.stringify(integrationsData), [integrationsData])

  const calculateShipping = useCallback(() => {
    const isShipperHQ = isShipperHQActive(integrations?.data)

    // For ShipperHQ clients: use the legacy fetchShippingQuote
    if (isShipperHQ) {
      const shouldCalculate = shouldCalculateShipping({
        isInitializing,
        isCartLoaded,
        cartItems,
        checkout,
        portal,
        integrations
      })

      if (!shouldCalculate) return

      const {
        selectedCustomerAddress,
        cartItems: items,
        multiAddressSelection
      } = createShippingParams({
        checkout,
        cartItems
      })

      dispatch(fetchShippingQuote(selectedCustomerAddress, items, multiAddressSelection))
    } else {
      // For non-ShipperHQ clients: use fetchShippingRates (ShipEngine with rate logging)
      if (isInitializing || !isCartLoaded) return
      if (!cartItems?.length) return

      const shippableItems = cartItems.filter(item => item.shippable_type !== 'Digital Delivery')
      if (shippableItems.length === 0) return

      // Skip if already calculating to avoid duplicate requests
      if (checkoutRates?.isCalculating) return

      // Build params using centralized service
      const params = buildShippingParams({
        selectedAddress: selectedCustomerAddress,
        cartItems: shippableItems,
        multiAddressSelection,
        isMultiAddressEnabled: portal?.multiple_address_shipping
      })

      if (params) {
        dispatch(fetchShippingRates(params))
      }
    }
  }, [
    isInitializing,
    isCartLoaded,
    selectedAddressKey,
    multiAddressSelectionKey,
    checkoutMode,
    multipleAddressShipping,
    integrationsDataKey,
    cartItemsKey,
    dispatch,
    checkoutRates?.isCalculating
  ])

  return { calculateShipping }
}
